// https://help.uploadcare.com/article/344-which-formats-are-considered-as-images
export const SUPPORTED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'heic',
  'webp'
]

export const SUPPORTED_IMGUR_VIDEO_EXTENSIONS = [
  'mp4',
  'gifv',
  'mov',
  'avi',
  'webm',
  'mkv',
  'mpg',
  'mpeg'
]

export const SUPPORTED_VIDEO_EXTENSIONS = [
  'mp4',
  'gifv',
  'mov',
  // 'avi',
  // 'wmv',
  'webm',
  // 'mkv',
  'm4v',
  '3gp',
  '3g2'
]

export const SUPPORTED_IMAGE_EDIT_EFFECTS = [
  'crop',
  'flip',
  'mirror',
  'rotate',
  'enhance',
  'blur',
  'grayscale',
  'invert'
]

// https://help.imgur.com/hc/en-us/articles/115000083326-What-files-can-I-upload-Is-there-a-size-limit-
// Reddit image max == 20 MB
// Reddit video max == 1 GB
const MB = 1024 * 1024
const GB = 1024 * MB
export const MAX_IMAGE_SIZE = 20 * MB
export const MAX_GIF_SIZE = 100 * MB
export const MAX_VIDEO_SIZE = 1 * GB

export function galleryImagesOnly(fileInfo) {
  if (fileInfo.isImage === false) {
    throw new Error('galleryImagesOnly')
  }
}

export function fileSizeLimit(imageMaxSize, videoMaxSize) {
  return function (fileInfo) {
    if (fileInfo.size === null) {
      return
    }

    const extension = fileInfo.name.toLowerCase().split('.').pop()

    // GIFs are technically images, so we need to check those first and return
    // before checking for other types of images, which only support up to 20 MB.
    if (extension === 'gif') {
      if (fileInfo.size > MAX_GIF_SIZE) {
        throw new Error('gifMaximumSize')
      } else {
        return
      }
    }

    if (SUPPORTED_IMAGE_EXTENSIONS.includes(extension)) {
      if (fileInfo.size > imageMaxSize) {
        throw new Error('imageMaximumSize')
      } else {
        return
      }
    }

    if (SUPPORTED_VIDEO_EXTENSIONS.includes(extension)) {
      if (fileInfo.size > videoMaxSize) {
        throw new Error('videoMaximumSize')
      } else {
        return
      }
    }
  }
}

export function fileTypeLimit(types) {
  return function (fileInfo) {
    if (fileInfo.name === null) {
      return
    }
    let extension = fileInfo.name.toLowerCase().split('.').pop()

    if (types.indexOf(extension) == -1) {
      throw new Error('fileType')
    }
  }
}

/**
 * Converts an Uploadcare fileInfo object to a MediaInput for usage in BulkCreateMedia.
 */
export function fileInfoToMediaInput(fileInfo) {
  return {
    externalId: fileInfo.uuid,
    name: fileInfo.name,
    mimeType: fileInfo.mimeType,
    size: fileInfo.size,
    url: fileInfo.cdnUrl,
    contentInfo: fileInfo.originalContentInfo || null,
    // Sometimes sourceInfo isn't available. I think this is mostly when uploading a large video.
    source: fileInfo.sourceInfo ? fileInfo.sourceInfo.source : 'uploaded'
  }
}

export function getMetadataFromProfile(profile, config) {
  return {
    userId: profile.hashedId,
    email: profile.email,
    name: profile.account.name || profile.fullName || profile.username,
    username: profile.username,
    planCode: profile.account.plan.code,
    planLabel: profile.account.plan.label,
    planMonthlyPrice: profile.account.plan.monthlyPrice,
    registeredAt: profile.registeredAt,
    timezone: profile.timezone,
    numRedditAccounts: profile.redditAccounts.length,
    appVersion: config.appVersion
  }
}

export function getOperation(url, opName) {
  const { operationStart, operationEnd } = getOperationStartAndEnd(url, opName)
  if (operationStart !== -1) {
    return url.substring(operationStart, operationEnd)
  }
}

export function removeOperation(url, opName) {
  const { operationStart, operationEnd } = getOperationStartAndEnd(url, opName)
  if (operationStart !== -1) {
    return url.substring(0, operationStart) + url.substring(operationEnd)
  }
  return url
}

export function getOperationStartAndEnd(url, opName) {
  let operationStart = url.indexOf(`-/${opName}/`)
  let operationEnd = url.indexOf('-/', operationStart + 1)
  if (operationEnd === -1) {
    // This is the last operation in the URL
    operationEnd = url.length
  }
  return { operationStart, operationEnd }
}

export function createOrReplaceOperation(url, opName, operation = '') {
  let opIndex = url.indexOf(`-/${opName}/`)

  // Make sure the operation ends with a slash but does not start with one
  if (operation && !operation.endsWith('/')) {
    operation += '/'
  }
  if (operation && operation.startsWith('/')) {
    operation = operation.substring(1)
  }

  let newURL = ''
  if (opIndex >= 0) {
    // Replace the operation in the URL
    let opEndIndex = url.indexOf('-/', opIndex + 1)
    if (opEndIndex === -1) {
      // This is the last operation in the URL
      opEndIndex = url.length
    }

    newURL =
      url.substring(0, opIndex) +
      `-/${opName}/${operation}` +
      url.substring(opEndIndex)
  } else {
    // Add the operation to the end
    newURL = url + `-/${opName}/${operation}`
  }

  return newURL
}

export function createImgurThumbnailLink(
  url,
  thumbnailSize = 'm',
  sizeInBytes = 0
) {
  // https://api.imgur.com/models/image#thumbs
  // Sizes:
  // s = Small Square (90x90)
  // b = Big Square (160x160)
  // t = Small Thumbnail (160x160)
  // m = Medium Thumbnail (320x320)
  // l = Large Thumbnail (640x640)
  // h = Huge Thumbnail (1024x1024)

  // Imgur API returns GIFs as thumbnails automatically
  // if they are larger than 20 MB
  if (url.endsWith('.gif') && sizeInBytes > 20971520) {
    return url
  }

  let parts = url.split('.')
  parts[parts.length - 2] = parts[parts.length - 2] + thumbnailSize
  return parts.join('.')
}

/**
 * Convert HEIC/HEIF to a JPEG format that can be handled by Imgur/RedGIFs.
 */
export function getSafeUploadLink(media) {
  let link = media.cdnUrl || media.url
  const isAppleImageFormat =
    media.mimeType.startsWith('image/heic') ||
    media.mimeType.startsWith('image/heif')

  // Convert HEIC/HEIF to a format that Imgur can handle
  if (isAppleImageFormat && !link.includes('-/preview/')) {
    link = `${link}-/preview/`
  }

  return link
}

/**
 * Convert a data URL to a File object.
 */
export function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, { type: mime })
}

window.UPLOADCARE_PUBLIC_KEY = 'f806c5d7aec9c40c7aa7'
window.UPLOADCARE_CROP = 'free, 1:1, 4:5, 9:16'

// https://github.com/uploadcare/uploadcare-widget/blob/master/src/locales/en.js
export const UPLOADCARE_LOCALE_TRANSLATIONS = {
  loadingInfo: 'Loading...',
  draghere: `
    <div>Drop files here</div>
    <div class="has-text-centered is-size-5 u-mt1">
      <span class="icon u-mx1"><i class="far fa-photo-video" aria-hidden="true"></i></span>
    </div>
  `,
  buttons: {
    choose: {
      files: {
        one: `
          <div>Drag & drop files</div>
          <div class="has-text-centered is-size-4 u-mt1">
            <span class="icon"><i class="far fa-image-user" aria-hidden="true"></i></span>
            <span class="icon u-mx1"><i class="far fa-video" aria-hidden="true"></i></span>
            <span class="icon"><i class="far fa-gif" aria-hidden="true"></i></span>
          </div>
        `,
        other: `
          <div>Add files</div>
          <div class="has-text-centered is-size-5 u-mt1">
            <span class="icon u-mx1"><i class="far fa-photo-video" aria-hidden="true"></i></span>
          </div>
        `
      }
    }
  },
  errors: {
    fileType: 'This type of file is not allowed.',
    imageMaximumSize: 'File is too large.',
    gifMaximumSize: 'File is too large.',
    videoMaximumSize: 'File is too large.',
    galleryImagesOnly: 'Only images are allowed in galleries.'
  },
  dialog: {
    tabs: {
      file: {
        drag: 'Drag and drop<br>your file here',
        nodrop: 'Upload images or videos from your device'
      },
      preview: {
        done: 'Save',
        image: {
          title: 'Edit image'
        },
        error: {
          fileType: {
            title: 'Invalid file type',
            text: `<p class="u-mb2">Postpone supports the following image/video types:</p>
            <p class="u-mb1"><span class="has-text-weight-bold">Images:</span> ${SUPPORTED_IMAGE_EXTENSIONS.join(
              ', '
            )}</p>
            <p><span class="has-text-weight-bold">Videos:</span> ${SUPPORTED_VIDEO_EXTENSIONS.join(
              ', '
            )}</p>`,
            back: 'Try again'
          },
          imageMaximumSize: {
            title: 'File too large',
            text: 'Images should be smaller than 20 MB',
            back: 'Try again'
          },
          gifMaximumSize: {
            title: 'File too large',
            text: 'GIFs should be smaller than 100 MB',
            back: 'Try again'
          },
          videoMaximumSize: {
            title: 'File too large',
            text: 'Videos should be smaller than 1 GB',
            back: 'Try again'
          }
        }
      }
    },
    url: {
      title: 'Images from the Web'
    }
  }
}

window.UPLOADCARE_LOCALE_TRANSLATIONS = UPLOADCARE_LOCALE_TRANSLATIONS
